import { ChatMessages, Features } from "@storytell/cp-client";
import type { MessageRetryData } from "@storytell/cp-client/src/gen/chat/v3/chat_messages_system_pb";
import { dispatchGlobalEvent } from "@storytell/events";
import { richTextToProseMirror } from "@storytell/rich-text/src/converters/richTextToProseMirror";
import type { UserId } from "@storytell/types";
import { produce } from "solid-js/store";
import {
  createEmptyStreamingChat,
  type SetStreamingStore,
  type StreamingBlockId,
  type StreamingChatId,
  type StreamingMessage,
  type StreamingMessageId,
  StreamingMessageStatus,
  type StreamingOperationId,
  type StreamingPrompt,
} from "../streamingTypes";

/**
 * Insert a message ID into a sorted array using binary search.
 * Optimized for the common case where messages arrive in order (append to end).
 *
 * Complexity:
 * - Best case (append): O(1) for comparison + O(n) for spread = O(n)
 * - Average case: O(log n) for search + O(n) for splice = O(n)
 *
 * This is more efficient than .sort() which is O(n log n) on every insertion.
 */
const insertMessageSorted = (
  messages: StreamingMessageId[],
  newMessageId: StreamingMessageId,
  getIndex: (id: StreamingMessageId) => number
): StreamingMessageId[] => {
  const newIndex = getIndex(newMessageId);

  if (messages.length === 0) {
    return [newMessageId];
  }

  const lastId = messages[messages.length - 1];
  const firstId = messages[0];

  // Fast path: new message belongs at the end (most common case during streaming)
  if (lastId !== undefined && newIndex >= getIndex(lastId)) {
    return [...messages, newMessageId];
  }

  if (firstId !== undefined && newIndex <= getIndex(firstId)) {
    return [newMessageId, ...messages];
  }

  let left = 0;
  let right = messages.length;
  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midId = messages[mid];
    if (midId !== undefined && getIndex(midId) < newIndex) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  const result = [...messages];
  result.splice(left, 0, newMessageId);
  return result;
};

/**
 * What the client knows about the turn it asked for, before the server has said anything about it.
 * A terminal event that arrives with no preceding MESSAGE_START is rebuilt from this. Undefined for
 * a chat's first turn, whose id is minted server-side and only reaches the client via MESSAGE_START.
 */
export type PendingTurn = {
  chatId: StreamingChatId;
  /** Without it the rebuilt message roots itself and drops out of a branched chat's active path. */
  parentMessageId?: StreamingMessageId;
  promptText: string;
  promptRichText?: StreamingPrompt["richText"];
};

/**
 * MESSAGE_START is the first event we receive for a message.
 */
export const processMessageStart = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
  onMessageStarted?: (messageId: StreamingMessageId) => void,
  overrideChatId?: StreamingChatId,
  sourceChatId?: StreamingChatId,
  sourceMessageId?: StreamingMessageId,
  submittedAt?: Date,
  parentMessageId?: StreamingMessageId
) => {
  if (message.data.case !== "messageStartData") {
    console.error(new Error(`Expected messageStartData, got ${message.data.case}`));
    return;
  }
  const {
    id,
    index,
    blockId,
    operationId,
    role,
    data: { value: data },
  } = message;
  // Use overrideChatId for forked chats, otherwise extract from event data
  const chatId = overrideChatId || (data.chatId as StreamingChatId);

  try {
    if (onChatIdCaptured) {
      onChatIdCaptured(chatId);
    }
  } catch (error) {
    console.error("Error on the callback for capturing chat ID", error);
  }

  const messageId = id as StreamingMessageId;

  try {
    if (onMessageStarted) {
      onMessageStarted(messageId);
    }
  } catch (error) {
    console.error("Error on the callback for capturing message start", error);
  }

  const streamingMessage: StreamingMessage = {
    id: messageId,
    index,
    role,
    chatId,
    operations: [],
    feature: data.feature,
    resolvedModel: data.resolvedModel,
    reroutePromisedModel: data.reroutePromisedModel,
    createdBy: data.userId as UserId,
    createdByEmail: data.userEmail?.trim() || undefined,
    status: StreamingMessageStatus.GENERATING,
    aggregateMetrics: {
      ingressTokens: 0,
      egressTokens: 0,
      ingressBytes: 0,
      egressBytes: 0,
      totalCredits: 0,
      ingressCredits: 0,
      egressCredits: 0,
    },
    prompt: {
      messageId,
      text: data.promptText,
      richText: data.promptRichText ? richTextToProseMirror(data.promptRichText) : undefined,
      blockId: blockId as StreamingBlockId,
      operationId: operationId as StreamingOperationId,
      scope: data.scope,
      resolvedMode: data.resolvedMode,
      selectedMode: data.selectedMode,
      cappedFromMode: data.cappedFromMode,
      vendorPreference: data.vendorPreference,
      vendorStrategy: data.vendorStrategy,
      preferredModel: data.preferredModel,
      suggestionAttribution: data.suggestionAttribution
        ? {
            persona: data.suggestionAttribution.persona,
            category: data.suggestionAttribution.category,
          }
        : undefined,
      preLoadedSkills: data.preLoadedSkills?.length ? data.preLoadedSkills : undefined,
      disabledTools: data.disabledTools ? { webSearch: data.disabledTools.webSearch } : undefined,
      attachments: data.attachments?.length
        ? data.attachments.map((a) => ({
            artifactId: a.artifactId,
            mimeType: a.mimeType,
            name: a.name,
            addToKnowledge: a.addToKnowledge,
          }))
        : undefined,
    },
    submittedAt,
    sourceChatId,
    sourceMessageId,
    parentMessageId: (data.parentMessageId as StreamingMessageId | undefined) || parentMessageId,
  };

  setStore(
    produce((store) => {
      if (!store.chats[chatId]) {
        store.chats[chatId] = createEmptyStreamingChat(chatId);
      }

      const existing = store.messages[messageId];
      if (existing?.parentMessageId && !streamingMessage.parentMessageId) {
        streamingMessage.parentMessageId = existing.parentMessageId;
      }

      store.messages[messageId] = streamingMessage;

      // Link any operations that arrived before this message
      const orphanedOps = store.orphanedOperations[messageId];
      if (orphanedOps) {
        const storedMsg = store.messages[messageId];
        if (storedMsg) {
          for (const opId of orphanedOps) {
            if (!storedMsg.operations.includes(opId)) {
              storedMsg.operations.push(opId);
            }
          }
        }
        delete store.orphanedOperations[messageId];
      }

      if (store.chats[chatId] && !store.chats[chatId].messages.includes(messageId)) {
        store.chats[chatId].messages = insertMessageSorted(
          store.chats[chatId].messages,
          messageId,
          (otherId) => store.messages[otherId]?.index || 0
        );
      }
    })
  );
};

export const processMessageStop = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  eventTimestamp?: Date
) => {
  const { id } = message;
  const messageId = id as StreamingMessageId;

  setStore(
    produce((store) => {
      const msg = store.messages[messageId];
      if (msg) {
        msg.status = StreamingMessageStatus.COMPLETED;

        // Mark any incomplete operations as failed (orphaned operations).
        // On recording replay eventTimestamp is the recorded stop time; using it
        // rather than "now" keeps reconstructed durations bounded to the original
        // session instead of spanning start-until-reload.
        for (const opId of msg.operations) {
          const operation = store.operations[opId];
          if (operation && !operation.isComplete) {
            operation.isComplete = true;
            operation.completedAt = eventTimestamp ?? new Date();
            if (
              operation.status !== ChatMessages.OperationStatus.COMPLETED &&
              operation.status !== ChatMessages.OperationStatus.FAILED &&
              operation.status !== ChatMessages.OperationStatus.CANCELLED
            ) {
              operation.status = ChatMessages.OperationStatus.FAILED;
            }
          }
        }
      }
    })
  );
};

export const processMessageError = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  onMessageFailed?: (messageId: StreamingMessageId) => void,
  pendingTurn?: PendingTurn
) => {
  if (message.data.case !== "messageErrorData") {
    console.error(new Error(`Expected messageErrorData, got ${message.data.case}`));
    return;
  }

  const { id, blockId: messageBlockId, operationId: messageOperationId, index: _index } = message;
  const originalMessageId = id as StreamingMessageId;
  const operationId = messageOperationId as StreamingOperationId;
  const data = message.data.value;
  const isFatal = data?.isFatal ?? true;
  let unattached = false;

  setStore(
    produce((store) => {
      const chatId = store.messages[originalMessageId]?.chatId ?? pendingTurn?.chatId;
      if (!chatId) {
        unattached = true;
        return;
      }

      let chat = store.chats[chatId];
      if (!chat) {
        chat = createEmptyStreamingChat(chatId);
        store.chats[chatId] = chat;
      }

      // A turn that dies before MESSAGE_START leaves nothing to hang the error on, and dropping it
      // is how a dead turn kept rendering as a live one. Rebuild the message and its operation from
      // the prompt the client still holds so the failure lands in the thread like any other.
      let originalMessage = store.messages[originalMessageId];
      if (!originalMessage) {
        originalMessage = {
          id: originalMessageId,
          chatId,
          feature: Features.Feature.UNSPECIFIED,
          index: chat.messages.length,
          role: ChatMessages.Role.ASSISTANT,
          prompt: {
            messageId: originalMessageId,
            blockId: messageBlockId as StreamingBlockId,
            operationId,
            text: pendingTurn?.promptText ?? "",
            richText: pendingTurn?.promptRichText,
            scope: undefined,
          },
          operations: [],
          status: StreamingMessageStatus.FAILED,
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
          },
          createdBy: "" as UserId,
          parentMessageId: pendingTurn?.parentMessageId,
        };
        store.messages[originalMessageId] = originalMessage;
        chat.messages = [...chat.messages, originalMessageId];

        // Operations that arrived before the message did are parked; a rebuilt message has to
        // adopt them the way a started one does, or the failed step stays invisible.
        const orphanedOps = store.orphanedOperations[originalMessageId];
        if (orphanedOps) {
          for (const opId of orphanedOps) {
            if (!originalMessage.operations.includes(opId)) {
              originalMessage.operations.push(opId);
            }
          }
          delete store.orphanedOperations[originalMessageId];
        }
      }

      let existingOperation = store.operations[operationId];
      if (!existingOperation) {
        existingOperation = {
          index: originalMessage.operations.length,
          operationId,
          isComplete: false,
          messageId: originalMessageId,
          blocks: [],
          name: "",
          activity: "error",
          startedAt: undefined,
          role: ChatMessages.Role.SYSTEM,
          status: ChatMessages.OperationStatus.FAILED,
          completedAt: undefined,
          task: undefined,
          progressMessage: undefined,
          parentOperationId: undefined,
        };
        store.operations[operationId] = existingOperation;
        originalMessage.operations.push(operationId);
      }

      const blockId = `block_${originalMessageId}_error_${Date.now()}` as StreamingBlockId;

      existingOperation.status = ChatMessages.OperationStatus.FAILED;
      existingOperation.isComplete = true;
      existingOperation.completedAt = new Date();

      if (data?.internal) {
        existingOperation.hasInternalError = true;
      }

      const streamingBlock = {
        type: "block" as const,
        isComplete: true,
        blockId,
        operationId,
        messageId: originalMessageId,
        role: ChatMessages.Role.SYSTEM,
        activity: "error",
        feature: originalMessage.feature,
        messageType: ChatMessages.MessageType.INTERSTITIAL_USER_FACING,
        title: "Error",
        finalContent: {
          type: "error" as const,
          displayMessage: data?.displayMessage || "An error occurred",
          systemDetail: data?.systemDetail,
          reason: data?.reason,
          isFatal,
          suggestedRetryModel: data?.suggestedRetryModel,
        },
        searchResults: undefined,
        webSearchResults: undefined,
        conceptResults: undefined,
        structuredResults: undefined,
        imageResults: undefined,
        metrics: undefined,
      };

      existingOperation.blocks.push(blockId);
      store.blocks[blockId] = streamingBlock;
      originalMessage.status = StreamingMessageStatus.FAILED;
    })
  );

  // The first turn of a chat carries no id the client has seen, so a failure before MESSAGE_START
  // has no thread to render in. The prompt bar takes it instead.
  if (unattached) {
    dispatchGlobalEvent("chat-turn-failed", { displayMessage: data?.displayMessage || undefined });
  }

  if (isFatal && onMessageFailed) {
    onMessageFailed(originalMessageId);
  }
};

export const processMessageRetry = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  if (message.data.case !== "messageRetryData") {
    console.error(new Error(`Expected messageRetryData, got ${message.data.case}`));
    return;
  }

  const { id, operationId: messageOperationId, index: _index } = message;
  const originalMessageId = id as StreamingMessageId;
  const operationId = messageOperationId as StreamingOperationId;

  setStore(
    produce((store) => {
      const originalMessage = store.messages[originalMessageId];
      const chatId = originalMessage?.chatId;
      if (!chatId) {
        console.error(new Error(`Chat not found for message retry: ${originalMessageId}`));
        return;
      }

      const existingOperation = store.operations[operationId];
      if (!existingOperation) {
        console.error(new Error(`Operation ${operationId} not found for retry message`));
        return;
      }

      const blockId = `block_${originalMessageId}_retry_${Date.now()}` as StreamingBlockId;

      existingOperation.status = ChatMessages.OperationStatus.RETRYING;

      const data = message.data.value as MessageRetryData;

      if (data?.internal) {
        existingOperation.hasInternalError = true;
      }
      const streamingBlock = {
        type: "block" as const,
        isComplete: true,
        blockId,
        operationId,
        messageId: originalMessageId,
        role: ChatMessages.Role.SYSTEM,
        activity: "retry",
        feature: originalMessage?.feature ?? Features.Feature.UNSPECIFIED,
        messageType: ChatMessages.MessageType.INTERSTITIAL_USER_FACING,
        title: "Refining results",
        finalContent: {
          type: "retry" as const,
          message: data?.message || "Retrying with adjusted parameters...",
        },
        searchResults: undefined,
        webSearchResults: undefined,
        conceptResults: undefined,
        structuredResults: undefined,
        imageResults: undefined,
        metrics: undefined,
      };

      if (!store.chats[chatId]) {
        store.chats[chatId] = createEmptyStreamingChat(chatId);
      }

      existingOperation.blocks.push(blockId);
      store.blocks[blockId] = streamingBlock;
    })
  );
};
